import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import type { HomeProofStat } from '@/data/shared/homeProofStats';

import CtaActionButton from '@/components/apps/ui/cta/CtaActionButton';
import { buildInquiryHref } from '@/components/apps/ui/inquiryForm/inquiryCtaSource';

import ProofStatCounter from './ProofStatCounter';
import { trackHomeProofCtaSelection } from './homeProofStatsAnalytics';

const PROCESS_CONSULT_HREF = buildInquiryHref({
  interest: 'process-consult',
  source: 'home-claim',
});

interface ProofStatEntryProps {
  stat: HomeProofStat;
  index: number;
  isActive: boolean;
  /** Intl locale tag for the figure shown inline on narrow screens */
  locale: string;
  setElement: (element: HTMLElement | null) => void;
}

/**
 * ### ProofStatEntry
 *
 * One entry in the scrolled column: the icon and kicker, the sentence, the
 * note on what the figure counts, and the press-kit figures it breaks down
 * into. On narrow screens, where there is no
 * sticky counter beside it, the entry shows its own figure and counts it up
 * on arrival. Entries away from the viewport's middle are dimmed so the eye
 * stays with the one the counter is showing.
 *
 * Every entry but the last reserves a screen of its own and centres itself in
 * it. The last one is only as tall as it reads, so the column ends where its
 * sentence ends: the half screen it would otherwise reserve is empty space the
 * sticky counter beside it would keep scrolling through, leaving the two
 * columns to finish at different points.
 *
 * @returns The rendered list item element
 */
export default function ProofStatEntry({
  stat,
  index,
  isActive,
  locale,
  setElement,
}: ProofStatEntryProps) {
  const { t } = useTranslation('common');

  return (
    <li
      ref={setElement}
      className={`flex flex-col justify-center py-16 transition-opacity duration-500 motion-reduce:transition-none 2xl:min-h-[64vh] 2xl:py-0 2xl:last:min-h-0 ${
        isActive ? 'opacity-100' : '2xl:opacity-35'
      }`}
      data-active={isActive || undefined}
    >
      <p className="text-[16px] font-bold uppercase leading-[140%] tracking-[0.65px] text-apps-brandGray900 sm:text-[18px] 2xl:text-[clamp(15px,1.2vw,20px)] 2xl:tracking-[0.75px]">
        <span className="mr-3 text-apps-brandGray800">
          {String(index + 1).padStart(2, '0')}
        </span>
        {stat.entryTitle ?? stat.kicker}
      </p>
      <p className="mt-5 text-[56px] font-bold leading-[100%] tracking-[-0.06em] text-apps-coreGrayWhite [font-variant-numeric:tabular-nums] sm:text-[76px] 2xl:hidden">
        <ProofStatCounter stat={stat} locale={locale} />
      </p>
      <p className="mt-7 max-w-[900px] text-pretty text-[22px] font-medium leading-[155%] tracking-[-0.34px] text-apps-brandGray900 sm:text-[28px] 2xl:mt-9 2xl:text-[clamp(24px,2.1vw,40px)] 2xl:leading-[150%]">
        {stat.description}
      </p>
      {stat.note && (
        <p className="mt-5 max-w-[820px] text-pretty text-[16px] font-medium leading-[160%] tracking-[-0.28px] text-apps-brandGray800 sm:text-[18px] 2xl:mt-6 2xl:text-[clamp(17px,1.3vw,22px)]">
          {stat.note}
        </p>
      )}
      {/*
       * What the headline figure breaks down into. It is also what keeps the
       * entry from being one sentence adrift in a screen of its own.
       */}
      <dl className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-[rgba(255,255,255,0.16)] bg-[rgba(255,255,255,0.16)] sm:grid-cols-3 2xl:mt-12">
        {stat.details.map((detail) => (
          <div
            key={detail.label}
            className="bg-apps-brandGrayBlack px-6 py-5 2xl:px-[clamp(24px,2.5vw,48px)] 2xl:py-[clamp(20px,2vw,32px)]"
          >
            <dt className="text-[20px] font-bold leading-[130%] tracking-[-0.34px] text-apps-coreGrayWhite sm:text-[22px] 2xl:text-[clamp(20px,1.7vw,32px)]">
              {detail.prefix && (
                <span className="mb-2 block text-[15px] font-medium italic leading-[150%] text-apps-brandGray800">
                  {detail.prefix}
                </span>
              )}
              {detail.value}
            </dt>
            <dd className="mt-2 text-pretty text-[15px] font-medium leading-[150%] tracking-[-0.26px] text-apps-brandGray800 2xl:text-[clamp(15px,1.1vw,18px)]">
              {detail.label}
            </dd>
          </div>
        ))}
      </dl>
      {index === 3 && (
        <div className="mt-10 grid w-full grid-cols-2 gap-3 sm:flex sm:max-w-[760px] sm:gap-4 2xl:mt-12">
          <CtaActionButton
            className="min-w-0 flex-1 justify-center px-3 text-center"
            href={PROCESS_CONSULT_HREF}
            tone="dark"
            onClick={() => trackHomeProofCtaSelection('process_consult')}
          >
            {t('Industrial 적용 상담')}
          </CtaActionButton>
          <CtaActionButton
            className="min-w-0 flex-1 justify-center px-3 text-center"
            href="/solution/industrial"
            tone="dark"
            variant="line"
            onClick={() => trackHomeProofCtaSelection('industrial_overview')}
          >
            {t('Industrial 보기')}
          </CtaActionButton>
        </div>
      )}
    </li>
  );
}
